import React from 'react';
import useOfferEdit from '../hooks/useOfferEdit';

const SpecialOfferForm = ({ park, existingOffer, onClose, onSuccess, onManageClick }) => {
  // Use the custom hook for all business logic
  const {
    formData,
    offerDuration,
    subscriptionMonths,
    existingSubscription,
    saving,
    setFormData,
    setOfferDuration,
    setSubscriptionMonths,
    handleSubmit,
    calculateExpirationDate,
    calculateTimeRemaining,
    isEditing
  } = useOfferEdit(park, existingOffer, {
    role: 'admin',
    onSuccess
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-emerald-600 p-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">
              {existingOffer ? '✏️ Edit' : '➕ Create'} Special Offer
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl leading-none"
            >
              ×
            </button>
          </div>
          <p className="text-white text-opacity-90 text-sm mt-1">{park.name}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Offer Title *
            </label>
            <input
              type="text"
              value={formData.offer_title}
              onChange={(e) => setFormData({...formData, offer_title: e.target.value})}
              placeholder="e.g., 20% Off Dog Grooming"
              maxLength={200}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Offer Description *
            </label>
            <textarea
              value={formData.offer_description}
              onChange={(e) => setFormData({...formData, offer_description: e.target.value})}
              placeholder="Describe the offer in detail..."
              maxLength={1000}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.offer_description.length}/1000 characters
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Terms & Conditions *
            </label>
            <textarea
              value={formData.offer_terms}
              onChange={(e) => setFormData({...formData, offer_terms: e.target.value})}
              placeholder="e.g., Valid for new customers only. Cannot be combined with other offers."
              maxLength={500}
              rows={3}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Required field - customers must accept terms before receiving the offer
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Offer Duration *
            </label>
            <select
              value={offerDuration}
              onChange={(e) => setOfferDuration(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="1week">1 Week</option>
              <option value="2weeks">2 Weeks</option>
              <option value="1month">1 Month</option>
              <option value="2months">2 Months</option>
              <option value="3months">3 Months</option>
              <option value="6months">6 Months</option>
              <option value="12months">12 Months</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Offer will expire on {new Date(calculateExpirationDate(offerDuration)).toLocaleDateString()}
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Email 
              <span className="text-gray-500 text-xs ml-2">(Optional - for Business Portal linking)</span>
            </label>
            <input
              type="email"
              value={formData.business_email}
              onChange={(e) => setFormData({...formData, business_email: e.target.value})}
              placeholder="business@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              💼 If provided, this offer will be linked to the business customer's portal dashboard
            </p>
          </div>


          {/* Subscription Period - only show if business email is provided */}
          {formData.business_email && (
            <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-purple-900 mb-2">
                📅 Subscription Period
              </label>
              <select
                value={subscriptionMonths}
                onChange={(e) => setSubscriptionMonths(e.target.value)}
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="3">3 Months</option>
                <option value="6">6 Months</option>
                <option value="9">9 Months</option>
                <option value="12">12 Months</option>
              </select>
              
              {/* Show time remaining if subscription exists */}
              {existingSubscription && (
                <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                  <p className="text-sm text-gray-700">
                    ⏱️ <span className="font-medium">Time Remaining:</span>{' '}
                    <span className="text-purple-700 font-semibold">
                      {calculateTimeRemaining(existingSubscription.end_date)}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Expires: {new Date(existingSubscription.end_date).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {/* Warning if shortening subscription */}
              {existingSubscription && parseInt(subscriptionMonths) < existingSubscription.months_purchased && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded">
                  <p className="text-sm text-amber-800">
                    ⚠️ <span className="font-medium">Warning:</span> This will shorten the existing subscription from{' '}
                    {existingSubscription.months_purchased} to {subscriptionMonths} months
                  </p>
                </div>
              )}
              
              <p className="text-xs text-gray-600 mt-2">
                💡 Subscription allows the business to access their portal and manage this offer
              </p>
            </div>
          )}


          {/* Actions */}
          <div className="space-y-3">
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : (existingOffer ? 'Update Offer' : 'Create Offer')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
            
            {/* Manage Offers Button - Only show if offer exists */}
            {existingOffer && onManageClick && (
              <button
                type="button"
                onClick={onManageClick}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <span>🗂️ Manage All Offers</span>
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpecialOfferForm;
